import { FormikValues } from "formik";

import { useTranslation } from "@/translations";

import {
  FormikAutocompleteSelectProps,
  IOptionItem,
} from "../fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "../fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { useCounselors } from "./hooks/useCounselors";

/**
 * The stored value of the "Alle Berater:innen" entry offered with `includeAllOption`. Not a counselor
 * id, so it must never reach an endpoint that expects one — callers translate it into "no counselor
 * filter" instead.
 */
export const ALL_COUNSELORS_OPTION_ID = "all";

/**
 * Everything {@link FormikAutocompleteSelect} accepts, except that the options are the counselor
 * list this component loads for itself. Derived from the underlying select's props rather than
 * re-declared, so a prop added there (`onValueChange`, say) is usable here without a second edit —
 * a hand-copied version silently omits it and the caller gets a type error for a prop that does
 * reach the DOM at runtime.
 */
type FormikCounselorSelectProps<T extends FormikValues> = DistributiveOmit<
  FormikAutocompleteSelectProps<T>,
  "options"
> & {
  options?: IOptionItem[];
  /** Offers "Alle Berater:innen" ({@link ALL_COUNSELORS_OPTION_ID}) ahead of the counselors. */
  includeAllOption?: boolean;
};

/**
 * `Omit` over a union collapses it into one object type. The field props are a union — either
 * `path` or `fieldName`, never both — so a plain `Omit` would make both optional and satisfy
 * neither branch. Distributing keeps the discrimination intact.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export function FormikCounselorSelect<T extends FormikValues>(props: FormikCounselorSelectProps<T>) {
  const { options: _options, includeAllOption = false, ...rest } = props;
  const { t } = useTranslation();

  const { counselorOptions } = useCounselors();

  const allOption: IOptionItem = {
    id: ALL_COUNSELORS_OPTION_ID,
    label: t("common.allCounselors"),
    displayValue: t("common.allCounselors"),
  };
  const options = includeAllOption ? [allOption, ...counselorOptions] : counselorOptions;

  return (
    <FormikAutocompleteSelect
      {...rest}
      options={options}
      getOptionLabel={(option) => option.displayValue ?? ""}
      renderOption={(renderProps, option) => <li {...renderProps}>{option.label}</li>}
    />
  );
}
